if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    constructor() {
      super();
      this.isFetching = false;
    }

    // connectedCallback wird aufgerufen, wenn das Element sicher im DOM ist
    connectedCallback() {
      // Event Delegation: Wir hoeren auf ALLE Klicks in diesem Bereich
      this.addEventListener('click', (event) => {
        const applyBtn = event.target.closest('.cart-discount__button');
        const removeBtn = event.target.closest('.cart-discount__pill-remove');

        if (applyBtn) {
          event.preventDefault();
          this.applyDiscount();
        } else if (removeBtn) {
          this.removeDiscount(event, removeBtn);
        }
      });

      // Enter-Taste im Eingabefeld abfangen
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
          const input = event.target.closest('#cart-discount');
          if (input) {
            event.preventDefault();
            this.applyDiscount();
          }
        }
      });
    }

    // Vollbild-Warenkorb (/cart, kein Drawer): die GANZE Section neu rendern
    // (Artikel + Summen + Discount-Box) — nur die Box zu ersetzen ließ die
    // Summen veraltet stehen (Review-Fund Fabian 2026-07-11).
    renderPageSection(data) {
      const sectionId = this.dataset.sectionId;
      const html = data.sections && data.sections[sectionId];
      if (!html) return;
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const newSection = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      const currentSection = document.getElementById(`shopify-section-${sectionId}`);
      if (!newSection || !currentSection) return;
      const wasOpen = !!this.closest('details')?.open;
      currentSection.innerHTML = newSection.innerHTML;
      if (wasOpen) {
        const details = currentSection.querySelector('cart-discount')?.closest('details');
        if (details) details.open = true;
      }
    }

    existingDiscounts() {
      return Array.from(this.querySelectorAll('.cart-discount__pill'))
        .map(pill => pill.dataset.discountCode)
        .filter(Boolean);
    }

    hideAllErrorMessages() {
      const err = this.querySelector('[ref="cartDiscountError"]');
      const errCode = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
      const errShip = this.querySelector('[ref="cartDiscountErrorShipping"]');
      
      if(err) err.classList.add('hidden');
      if(errCode) errCode.classList.add('hidden');
      if(errShip) errShip.classList.add('hidden');
    }

    handleDiscountError(type) {
      this.hideAllErrorMessages();
      const err = this.querySelector('[ref="cartDiscountError"]');
      const target = type === 'discount_code' 
        ? this.querySelector('[ref="cartDiscountErrorDiscountCode"]') 
        : this.querySelector('[ref="cartDiscountErrorShipping"]');
        
      if(err) err.classList.remove('hidden');
      if(target) target.classList.remove('hidden');
    }

    async applyDiscount() {
      const discountInput = this.querySelector('input[name="discount"]');
      const discountCodeValue = discountInput?.value?.trim();
      
      if (this.isFetching || !discountCodeValue) return;

      // TRACKING START: Rabattcode Versuch erfassen
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'discount_attempt',
        'discount_code': discountCodeValue
      });
      // TRACKING ENDE

      const existingDiscounts = this.existingDiscounts();
      if (existingDiscounts.map(d => d.toLowerCase()).includes(discountCodeValue.toLowerCase())) {
        discountInput.value = ''; // Code ist schon im Warenkorb
        return;
      }

      this.isFetching = true;
      const submitButton = this.querySelector('.cart-discount__button');
      if (submitButton) submitButton.style.opacity = "0.5"; // Visuelles Feedback (Laden)

      try {
        const drawer = document.querySelector('cart-drawer');
        let sectionsToRequest = this.dataset.sectionId;
        
        // Wir fragen den Drawer nach den benoetigten Sections
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          sectionsToRequest = drawer.getSectionsToRender().map(s => s.section).join(',');
        }

        const requestBody = {
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: sectionsToRequest
        };

        const response = await fetch(`${theme.routes.cartUpdate}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) throw new Error('API Fehler');
        const data = await response.json();

        // Prüfen, ob Shopify den Code ablehnt
        const isNotApplicable = (data.discount_codes || []).find(
          (discount) => discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false
        );

        if (isNotApplicable) {
          discountInput.value = '';
          this.handleDiscountError('discount_code');
          
          // TRACKING: Ungültiger Code
          window.dataLayer.push({
            'event': 'discount_invalid',
            'discount_code': discountCodeValue
          });

        } else {
          // Rabatt erfolgreich! Daten direkt an den Drawer übergeben
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data, false);
          } else {
            // Fallback für Vollbild-Warenkorb
            this.renderPageSection(data);
          }
          
          // TRACKING: Gültiger Code
          window.dataLayer.push({
            'event': 'discount_success',
            'discount_code': discountCodeValue
          });
        }
      } catch (error) {
        console.error('Fehler beim Anwenden des Rabatts:', error);
      } finally {
        this.isFetching = false;
        if (submitButton) submitButton.style.opacity = "1";
      }
    }

    async removeDiscount(event, removeBtn) {
      const pill = removeBtn.closest('.cart-discount__pill');
      if (!pill) return;

      const codeToRemove = pill.dataset.discountCode;
      if (!codeToRemove) return;

      const remainingDiscounts = this.existingDiscounts().filter(code => code !== codeToRemove);
      
      this.isFetching = true;
      pill.style.opacity = "0.5";

      try {
        const drawer = document.querySelector('cart-drawer');
        let sectionsToRequest = this.dataset.sectionId;
        
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          sectionsToRequest = drawer.getSectionsToRender().map(s => s.section).join(',');
        }

        const requestBody = {
          discount: remainingDiscounts.join(','),
          sections: sectionsToRequest
        };

        const response = await fetch(`${theme.routes.cartUpdate}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) throw new Error('API Fehler');
        const data = await response.json();

        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data, false);
        } else {
          this.renderPageSection(data);
        }
      } catch (error) {
        console.error('Fehler beim Entfernen des Rabatts:', error);
        pill.style.opacity = "1";
      } finally {
        this.isFetching = false;
      }
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
